import { useCallback, useMemo } from "react";
import type { GroupMethod } from "store/analyticsStore";
import { fetchAnalyticsCachedSales, getStoreKeyFromFilters } from "store/analyticsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { AnalyticsFilters } from "types";

const getFetchedPages = <T>(pagesAsMap: Record<string, T>) => {
    return Object.keys(pagesAsMap)
        .map((str) => parseInt(str, 10))
        .filter(Boolean)
        .sort();
};

const useAnalyticsData = ({
    filters,
    groupMethods,
}: {
    companyId?: number;
    filters: AnalyticsFilters;
    groupMethods?: GroupMethod[];
}) => {
    const dispatch = useAppDispatch();

    const { analyticsCachedSales, analyticsCachedSalesStatus } = useAppSelector((state) => state.analytics);

    const storeKey = useMemo(() => getStoreKeyFromFilters(filters, groupMethods), [filters, groupMethods]);

    const fetchNextPage = useCallback(() => {
        dispatch(
            fetchAnalyticsCachedSales({
                ...filters,
                groupMethods,
                storeKey,
                limit: 500,
            }),
        );
    }, [filters, groupMethods, storeKey, dispatch]);

    const mergedData = useMemo(() => {
        const data = analyticsCachedSales[storeKey];

        if (!data?.pages) {
            return [];
        }

        const mergedPages = getFetchedPages(data.pages)
            .map((pageNum) => data.pages?.[pageNum])
            .flat(1)
            .filter(Boolean);

        return mergedPages;
    }, [storeKey, analyticsCachedSales]);

    const status = analyticsCachedSalesStatus[storeKey];

    return {
        fetchNextPage,
        data: mergedData,
        status,
    };
};

export default useAnalyticsData;
